import React from 'react';
import { Calculation, Download } from '@carbon/react/icons';
import { Button } from '@carbon/react';
import { isNil, isEmpty, isNumber, toString } from 'lodash-es';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { downloadBlob } from 'utils/download-blob';
import tableActionStyles from 'components/table/table-actions.module.scss';

import { FormattedCell } from './shared-data-table';
import {
  UnitInterestCard,
  AcreageUnitInterestFormula,
  LateralUnitInterestFormula,
} from 'components/unit-interest-card';
import styles from './shared-data-table.module.scss';
import { CRMAssetResponse, TitleAssetResponse } from 'types/api-responses';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import classNames from 'classnames';
export const NUMERIC_FIELDS = [
  'unitNri',
  'unitWi',
  'valueAc',
  'acres',
  'netAcres',
  'subtractGrossAcres',
  'subtractNri',
  'subtractAcres',
  'grossAcres',
  'salesforceNetAcres',
  'subtractNetAcres',
  'surface',
  'startDepth',
  'endDepth',
  'royaltyRate',
  'participationFactor',
  'participationTractAc',
  'executive',
  'bonus',
  'delay',
  'royalty',
  'fixed',
  'wi',
  'orr',
  'grossAcreage',
  'legalAcreage',
];

export const CLICKABLE_FIELDS = {
  TRACT: 'crmTractName',
  SUBTRACT: 'crmSubtractName',
};

export const getUnitNriFromAllocationMethod = (
  activeAllocationMethod: 'acreage' | 'lateral_length',
  row: CRMAssetResponse | TitleAssetResponse
) => {
  if (activeAllocationMethod === 'lateral_length') {
    return row.lateralUnitNri ?? 0.0;
  }
  if (activeAllocationMethod === 'acreage') {
    return row.acreageUnitNri ?? 0.0;
  }
  return 0.0;
};

export const renderNumericValue = (
  value: any,
  precision: number = 8,
  isSubtotal: boolean = false
): React.ReactNode => {
  return (
    <div
      className={`${styles.cellWrapper} ${isSubtotal ? styles.subtotal : ''}`}
    >
      {formatNumberWithTooltip(value, precision, '--')}
    </div>
  );
};

export const renderTableCellValue = (
  key: string,
  value: any,
  row?: any
): React.ReactNode => {
  const isSubtotal = row && row.isSubtotal;

  if (NUMERIC_FIELDS.includes(key) && !isNil(value)) {
    return renderNumericValue(value, 8, isSubtotal);
  }

  if (isSubtotal) {
    return <div className={styles.boldText}>{value}</div>;
  }

  return value;
};

export const createClickableCell = (
  value: string | null | undefined,
  onClick: () => void
): FormattedCell => {
  return {
    content: (
      <span
        style={{
          cursor: 'pointer',
          color: '#0f62fe',
          textDecoration: 'underline',
        }}
      >
        {String(value || '')}
      </span>
    ),
    onClick,
  };
};

export const buildUnitWiCalculation = (
  activeAllocationMethod: 'acreage' | 'lateral_length',
  formulaMetadata: AcreageUnitInterestFormula | LateralUnitInterestFormula,
  wiValue: number
): JSX.Element => {
  const workingInterest = formulaMetadata.wi ?? 0;
  const participationTractAc =
    'participationTractAc' in formulaMetadata
      ? typeof formulaMetadata.participationTractAc === 'string'
        ? parseFloat(formulaMetadata.participationTractAc)
        : (formulaMetadata.participationTractAc ?? 0)
      : 0;
  const uatGisAc = formulaMetadata.uatGisAc ?? 0;

  if (activeAllocationMethod === 'lateral_length') {
    const totalLlFt =
      (formulaMetadata as LateralUnitInterestFormula).totalLlFt ?? 0;
    const uatFt = (formulaMetadata as LateralUnitInterestFormula).uatFt ?? 0;

    return (
      <span data-testid="unit-wi-calculation">
        {formatNumberWithTooltip(workingInterest, 8, '0.00000000', false)} * (
        {formatNumberWithTooltip(uatFt, 2, '0.00', false, 'ft')} /{' '}
        {formatNumberWithTooltip(totalLlFt, 2, '0.00', false, 'ft')}) * (
        {formatNumberWithTooltip(participationTractAc, 2, '0.00', false, 'ac')}{' '}
        / {formatNumberWithTooltip(uatGisAc, 2, '0.00', false, 'ac')}) ={' '}
        {formatNumberWithTooltip(wiValue, 8, '0.00000000', false)}
      </span>
    );
  } else {
    const uatAc = (formulaMetadata as AcreageUnitInterestFormula).uatAc ?? 0;
    const unitTractGrossAcresAc =
      (formulaMetadata as AcreageUnitInterestFormula).unitTractGrossAcresAc ??
      0;

    return (
      <span data-testid="unit-wi-calculation">
        {formatNumberWithTooltip(workingInterest, 8, '0.00000000', false)} * (
        {formatNumberWithTooltip(uatAc, 2, '0.00', false, 'ac')} /{' '}
        {formatNumberWithTooltip(unitTractGrossAcresAc, 2, '0.00', false, 'ac')}
        ) * (
        {formatNumberWithTooltip(participationTractAc, 2, '0.00', false, 'ac')}{' '}
        / {formatNumberWithTooltip(uatGisAc, 2, '0.00', false, 'ac')}) ={' '}
        {formatNumberWithTooltip(wiValue, 8, '0.00000000', false)}
      </span>
    );
  }
};

export const createUnitInterestCell = (
  value: any,
  allocationMethod: 'acreage' | 'lateral_length',
  hasAllocationManager: boolean,
  title: string = 'Unit NRI Calculation',
  participationFactorOverridden: boolean
): React.ReactNode => {
  if (isNil(value)) {
    return null;
  }

  const unitInterestValue = value?.value ?? 0;
  const calculationMethod = value?.calculationMethod ?? 'nra';
  const isSubtotal = value?.isSubtotal ?? false;
  const lateralUnitInterestFormula = value?.lateralUnitInterestFormula;
  const acreageUnitInterestFormula = value?.acreageUnitInterestFormula;
  const tractNriFormula = value?.tractNriFormula;

  if (isSubtotal) {
    return (
      <div className={styles.boldText}>
        {formatNumberWithTooltip(unitInterestValue, 8)}
      </div>
    );
  }

  const renderTrigger = (active: boolean, toggle: () => void) => (
    <CalculationIconTrigger
      definition="Show Calculation"
      active={active}
      onClick={toggle}
    >
      <div className={styles.iconContainer}>
        <Calculation
          className={styles.calculationIcon}
          aria-hidden="true"
          role="img"
        />
      </div>
    </CalculationIconTrigger>
  );

  return (
    <div className={styles.nriCellWrapper}>
      <span className={styles.numericCell}>
        {formatNumberWithTooltip(unitInterestValue, 8)}
      </span>
      <UnitInterestCard
        unitInterestValue={unitInterestValue}
        activeAllocationMethod={allocationMethod}
        lateralUnitInterestFormula={lateralUnitInterestFormula}
        acreageUnitInterestFormula={acreageUnitInterestFormula}
        hasAllocationManager={hasAllocationManager}
        calculationMethod={calculationMethod}
        tractNRIFormula={tractNriFormula}
        renderTrigger={renderTrigger}
        title={title}
        participationFactorOverridden={participationFactorOverridden}
      />
    </div>
  );
};

export const exportToCsv = <T extends Record<string, any>>(
  data: T[],
  headers: { key: string; header: string }[],
  filename: string
): void => {
  if (isEmpty(data)) return;

  const headerRow = headers.map((column) => `"${column.header}"`).join(',');
  const dataRows = data.map((row) => {
    return headers
      .map((column) => {
        const value = row[column.key];

        const formatted = !isNil(value)
          ? isNumber(value)
            ? value
            : `"${toString(value).replace(/"/g, '""')}"`
          : '""';
        return formatted;
      })
      .join(',');
  });

  const csvContent = [headerRow, ...dataRows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
};

export const ExportToCsvButton = <T extends Record<string, any>>(props: {
  rows: T[];
  headers: { key: string; header: string }[];
  filename: string;
  className?: string;
}) => {
  const { rows, headers, filename, className } = props;

  return (
    <Button
      kind="ghost"
      size="sm"
      renderIcon={Download}
      iconDescription="Download as CSV"
      hasIconOnly
      tooltipPosition="bottom"
      tooltipAlignment="center"
      onClick={() => exportToCsv(rows, headers, filename)}
      className={classNames(tableActionStyles.actionButton, className)}
    />
  );
};
